import * as React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

import type { Group } from "@/types/domain.types"
import { logger } from "@/lib/logger"

const groupFormSchema = z.object({
  name: z.string().min(1, "Group name is required").max(100, "Name too long"),
  description: z.string().max(500, "Description too long").optional(),
  emoji: z.string().optional(),
  currency: z.string().min(1),
})

type GroupFormData = z.infer<typeof groupFormSchema>

interface GroupFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group?: Group
  onSubmit: (data: GroupFormData) => Promise<void>
  isSubmitting?: boolean
}

const COMMON_EMOJIS = [
  "🏠", "👨‍👩‍👧‍👦", "🎉", "✈️", "🍕", "🛒", "🎵", "💼", "🎯", "💰",
  "🏖️", "🎲", "📚", "🏥", "🚗", "🏃‍♂️", "🎨", "🌟", "🔥", "❤️"
]

const COMMON_CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CAD", name: "Canadian Dollar", symbol: "$" },
  { code: "AUD", name: "Australian Dollar", symbol: "$" },
  { code: "CHF", name: "Swiss Franc", symbol: "Fr" },
  { code: "SEK", name: "Swedish Krona", symbol: "kr" },
]

export const GroupForm: React.FC<GroupFormProps> = ({
  open,
  onOpenChange,
  group,
  onSubmit,
  isSubmitting = false,
}) => {
  const [selectedEmoji, setSelectedEmoji] = React.useState("")

  const form = useForm<GroupFormData>({
    resolver: zodResolver(groupFormSchema),
    defaultValues: {
      name: "",
      description: "",
      emoji: "",
      currency: "EUR",
    },
  })

  // Reset form when group changes or dialog opens
  React.useEffect(() => {
    if (open) {
      form.reset({
        name: group?.name || "",
        description: group?.description || "",
        emoji: group?.emoji || "",
        currency: group?.currency || "EUR",
      })
      setSelectedEmoji(group?.emoji || "")
    }
  }, [open, group, form])

  React.useEffect(() => {
    form.setValue("emoji", selectedEmoji)
  }, [selectedEmoji, form])

  const handleSubmit = async (data: GroupFormData) => {
    try {
      await onSubmit(data)
      onOpenChange(false)
      if (!group) {
        form.reset()
        setSelectedEmoji("")
      }
    } catch (error) {
      logger.error("Failed to submit group:", error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {group ? "Edit Group" : "Create New Group"}
          </DialogTitle>
          <DialogDescription>
            {group
              ? "Update your group details below."
              : "Create a new group to start tracking shared expenses."
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Group Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Roommates, Vacation 2024"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="What is this group for?"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Add a brief description to help identify this group.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-3">
              <FormLabel>Group Icon (Optional)</FormLabel>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={selectedEmoji === "" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedEmoji("")}
                >
                  None
                </Button>
                {COMMON_EMOJIS.map((emoji) => (
                  <Button
                    key={emoji}
                    type="button"
                    variant={selectedEmoji === emoji ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedEmoji(emoji)}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            </div>

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_CURRENCIES.map((currency) => (
                      <Button
                        key={currency.code}
                        type="button"
                        variant={field.value === currency.code ? "default" : "outline"}
                        size="sm"
                        onClick={() => field.onChange(currency.code)}
                      >
                        <span className="mr-1">{currency.symbol}</span>
                        {currency.code}
                      </Button>
                    ))}
                  </div>
                  <FormDescription>
                    This will be the default currency for expenses in this group.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? "Saving..."
                  : group
                    ? "Update Group"
                    : "Create Group"
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}